import {useEffect} from "react";
import {useAppDispatch} from "../../../hooks/redux";
import socketService from "../../../middleware/websocketMiddleware";
import {carRemoved, carUpserted, colorsReplaced, eventReceived} from "./slice";
import {fetchAfterReconnect} from "./actions";
import {ICar, ICarColor, ICarEvent} from "./types";
import {milestoneNotify, stageArrivalNotify} from "../../notify/model/actions";

/**
 * Живые обновления доски. Клиент уже в комнате компании (`useNotifications`
 * шлёт `notification:join` на каждый connect). После переподключения доска
 * перечитывается целиком (и открытые журнал и история): пропущенные события не догоняются.
 * `onOpen` — клик по уведомлению «машина пришла на наш этап» (S4.2);
 * `notify = false` — без этих уведомлений (способность `notifications`, ROLES.md § 4).
 */
export function useBoardSocket(onOpen: (carId: string) => void, notify = true) {
    const dispatch = useAppDispatch();

    useEffect(() => {
        const socket = socketService.connect();
        let wasConnected = socket.connected;

        const onCar = (car: ICar) => dispatch(carUpserted(car));
        const onRemoved = (p: {id: string}) => dispatch(carRemoved(p));
        const onEvent = (e: ICarEvent) => {
            dispatch(eventReceived(e));
            if (notify) {
                dispatch(stageArrivalNotify(e, onOpen));
                dispatch(milestoneNotify(e, onOpen));
            }
        };
        const onColors = (colors: ICarColor[]) => dispatch(colorsReplaced(colors));
        const onConnect = () => {
            if (wasConnected) dispatch(fetchAfterReconnect());
            wasConnected = true;
        };

        socket.on("board:car", onCar);
        socket.on("board:car_removed", onRemoved);
        socket.on("board:event", onEvent);
        socket.on("board:colors", onColors);
        socket.on("connect", onConnect);
        return () => {
            socket.off("board:car", onCar);
            socket.off("board:car_removed", onRemoved);
            socket.off("board:event", onEvent);
            socket.off("board:colors", onColors);
            socket.off("connect", onConnect);
        };
    }, [dispatch, onOpen, notify]);
}
